/**
 * @chordbaker/design - Colors
 *
 * Shared color definitions for ChordBaker applications.
 * Single source of truth for light and dark theme color palettes.
 */
export const lightColors = {
    // Background colors
    background: '#FFFFFF',
    surface: '#F8FAFC',
    surfaceSecondary: '#F1F5F9',
    surfaceTertiary: '#E2E8F0',
    // Text colors
    text: '#1E293B',
    textSecondary: '#475569',
    textTertiary: '#64748B',
    textInverse: '#FFFFFF',
    // Action colors
    primary: '#3B82F6',
    secondary: '#6B7280',
    success: '#10B981',
    warning: '#D97706', // amber-600 — same ramp as the button tiers
    error: '#EF4444',
    info: '#14B8A6',
    notes: '#7F4BB1', // violet — tones
    guitar: '#DA3882', // muted pink — shape diagrams
    guitarCustom: '#B52C70', // the shape pink a shade deeper (5.6:1 on surface) — user-made shapes
    piano: '#DA3882', // == guitar — separate token so it can diverge
    pianoCustom: '#B52C70', // == guitarCustom — user-made shapes
    lyrics: '#955900', // amber, darkened for light-mode readability
    lyricsMode: '#007781', // teal — Performance View toggle/icon
    johan: '#3B82F6', // == primary (Johan wears the Edit view's blue)
    key: '#137D41', // green — degrees + key chip (NOT the success green)
    keySecondary: '#B8862B', // muted gold — selected accidental tile
    keyTertiary: '#C4653A', // muted orange — selected mode tile
    beats: '#797600', // yellow, darkened for light-mode readability — beat dots, Beats toggle
    border: '#E2E8F0',
    borderSecondary: '#CBD5E1',
    // Button variants
    buttonPrimary: '#3B82F6',
    buttonPrimaryHover: '#2563EB',
    buttonPrimaryPressed: '#1D4ED8',
    buttonSecondary: '#6B7280',
    buttonSecondaryHover: '#4B5563',
    buttonSecondaryPressed: '#374151',
    buttonSuccess: '#10B981',
    buttonSuccessHover: '#059669',
    buttonSuccessPressed: '#047857',
    buttonWarning: '#D97706', // amber-600
    buttonWarningHover: '#B45309', // amber-700
    buttonWarningPressed: '#92400E', // amber-800
    buttonError: '#EF4444',
    buttonErrorHover: '#DC2626',
    buttonErrorPressed: '#B91C1C',
    buttonInfo: '#14B8A6',
    buttonInfoHover: '#0D9488',
    buttonInfoPressed: '#0F766E',
    buttonDisabled: '#CBD5E1', // neutral gray — was pink, read as error
    // Focus ring
    focusRing: 'rgba(59, 130, 246, 0.35)', // primary @ 35%
    surfaceTint: 'rgba(59, 130, 246, 0.06)',
    errorTint: 'rgba(239, 68, 68, 0.12)',
    backdrop: 'rgba(0, 0, 0, 0.1)',
    scrollbarTrack: 'rgba(30, 41, 59, 0.1)',
    scrollbarThumb: 'rgba(30, 41, 59, 0.3)',
    // Slot specific colors
    slotBackground: '#FFFFFF',
    slotBorder: '#E2E8F0',
    slotActive: '#3B82F6',
    slotDivider: '#CBD5E1',
    // Modal colors
    modalBackground: '#FFFFFF',
    modalOverlay: 'rgba(0, 0, 0, 0.5)',
    modalOverlayHeavy: 'rgba(0, 0, 0, 0.7)',
    // Header colors
    headerBackground: '#FFFFFF',
    headerText: '#1E293B',
    headerBorder: '#E2E8F0',
};
export const darkColors = {
    // Background colors
    background: '#0F172A',
    surface: '#1E293B',
    surfaceSecondary: '#334155',
    surfaceTertiary: '#475569',
    // Text colors
    text: '#F8FAFC',
    textSecondary: '#CBD5E1',
    textTertiary: '#94A3B8',
    textInverse: '#1E293B',
    // Action colors
    primary: '#60A5FA',
    secondary: '#9CA3AF',
    success: '#34D399',
    warning: '#FBBF24', // amber-400 — same ramp as the button tiers
    error: '#F87171',
    info: '#2DD4BF',
    notes: '#D5B2FE', // violet — tones
    guitar: '#F27FB7', // muted pink — shape diagrams
    guitarCustom: '#F9A8CE', // the shape pink a shade lighter — user-made shapes
    piano: '#F27FB7', // == guitar — separate token so it can diverge
    pianoCustom: '#F9A8CE', // == guitarCustom — user-made shapes
    lyrics: '#FFBB74', // amber
    lyricsMode: '#6CD7E3', // teal — Performance View toggle/icon
    johan: '#60A5FA', // == primary (Johan wears the Edit view's blue)
    key: '#83E4A6', // green — degrees + key chip (NOT the success green)
    keySecondary: '#E3BC5C', // muted gold — selected accidental tile
    keyTertiary: '#EFA36A', // muted orange — selected mode tile
    beats: '#DAD862', // yellow — beat dots, Beats toggle
    border: '#475569',
    borderSecondary: '#64748B',
    // Button variants
    buttonPrimary: '#60A5FA',
    buttonPrimaryHover: '#3B82F6',
    buttonPrimaryPressed: '#2563EB',
    buttonSecondary: '#9CA3AF',
    buttonSecondaryHover: '#6B7280',
    buttonSecondaryPressed: '#4B5563',
    buttonSuccess: '#34D399',
    buttonSuccessHover: '#10B981',
    buttonSuccessPressed: '#059669',
    buttonWarning: '#FBBF24', // amber-400
    buttonWarningHover: '#F59E0B', // amber-500
    buttonWarningPressed: '#D97706', // amber-600
    buttonError: '#F87171',
    buttonErrorHover: '#EF4444',
    buttonErrorPressed: '#DC2626',
    buttonInfo: '#2DD4BF',
    buttonInfoHover: '#14B8A6',
    buttonInfoPressed: '#0D9488',
    buttonDisabled: '#334155', // neutral slate — was deep red, read as error
    // Focus ring
    focusRing: 'rgba(96, 165, 250, 0.35)',
    surfaceTint: 'rgba(96, 165, 250, 0.06)',
    errorTint: 'rgba(248, 113, 113, 0.12)',
    backdrop: 'rgba(0, 0, 0, 0.1)',
    scrollbarTrack: 'rgba(248, 250, 252, 0.1)',
    scrollbarThumb: 'rgba(248, 250, 252, 0.3)',
    // Slot specific colors
    slotBackground: '#0F172A',
    slotBorder: '#475569',
    slotActive: '#60A5FA',
    slotDivider: '#64748B',
    // Modal colors
    modalBackground: '#1E293B',
    modalOverlay: 'rgba(0, 0, 0, 0.7)',
    modalOverlayHeavy: 'rgba(0, 0, 0, 0.85)',
    // Header colors
    headerBackground: '#0F172A',
    headerText: '#F8FAFC',
    headerBorder: '#475569',
};
